/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Grid, Card, Icon, Typography, CardContent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { UpdateChangeDtoStatusEnum } from '@codefluegel/zeta-change-typescript-client';
import { useAuth } from 'react-oidc-context';
import { useChangeApiFactory, useUserApiFactory } from '../../../ApiClient';
import { TaskCardLocalizationList } from '../../../constants/changes';

/**
 * The internal dependencies
 */
import TaskCardIconMap from '../../../utils/changes';
import { getChangeLinkByStatus } from '../../../utils/utils';

type NotificationCardProps = {
  Title: string;
  ChangeId: string;
  ChangeNoClient: string;
  ChangeNo: string;
  Status: UpdateChangeDtoStatusEnum;
  ProjectId: string;
  NotificationId: number;
  read: boolean;
  navigateToChange?: (url: string) => void;
};

const NotificationCard = observer(
  ({
    Title,
    ChangeNoClient,
    ChangeId,
    Status,
    ChangeNo,
    read,
    navigateToChange,
  }: NotificationCardProps) => {
    const TaskCardLocalization = TaskCardLocalizationList();

    const auth = useAuth();
    const user = auth.user?.profile.email?.toLocaleLowerCase();

    const userApi = useUserApiFactory();
    const changeApi = useChangeApiFactory();

    const markRead = async () => {
      try {
        if (!read && user) {
          const { data: notifications } =
            await userApi.usersControllerFindAllNotifications({
              id: user,
              onlyUnread: true,
            });

          const changeNotifications = notifications.filter(
            notification => notification.changeRequest.id === Number(ChangeId),
          );

          const markReadPromises = changeNotifications.map(notification =>
            userApi.usersControllerMarkNotificationRead({
              id: user,
              notificationId: notification.id,
            }),
          );
          await Promise.all(markReadPromises);
        }
        const { data: change } = await changeApi.changesControllerFindOne({
          id: Number(ChangeId),
        });
        const link = getChangeLinkByStatus(change.status);

        if (navigateToChange)
          navigateToChange(`/change-request/${ChangeId}/${link}`);
      } catch (e) {
        console.log(e);
      }
    };

    return (
      <Card
        sx={{
          ':hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          },
        }}
        variant="outlined"
        onClick={markRead}
        css={css`
          background-color: transparent;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          box-shadow: none;
          transition: box-shadow 0.3s ease-in-out;
          cursor: pointer;
          overflow: hidden;
        `}
      >
        <CardContent
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 8px !important;
            background: #f9fafb;
          `}
        >
          {/* Left Side: Title and IDs */}
          <Grid
            container
            direction="column"
            gap={1}
            css={css`
              flex: 1;
            `}
          >
            <Grid>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  wordBreak: 'break-word',
                  fontSize: '1rem',
                  lineHeight: 1.2,
                }}
              >
                {Title}
              </Typography>
            </Grid>
            <Grid
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <Icon fontSize="small">{TaskCardIconMap[Status]}</Icon>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                }}
              >
                {TaskCardLocalization[Status]}
              </Typography>
            </Grid>
            <Grid>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                {ChangeNo}
              </Typography>
            </Grid>
            <Grid>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                {ChangeNoClient}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  },
);

export default NotificationCard;
